import React, { useEffect, useState } from 'react';

import { useFormikContext } from 'formik';

import Input from 'components/shared/Input/Input';
import Switch from 'components/shared/Switch/Switch';
import { showAlert } from 'components/shared/Alert/Alert';
import Allowed from 'components/shared/Permissions/Allowed';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import SectionList from 'components/practice/UserPreferences/components/SectionList';

const OperationalSettings = ({ setShowTechnologyModal }) => {
  const { setFieldValue, values } = useFormikContext();

  const [feeInput, setFeeInput] = useState('');

  useEffect(() => {
    setFeeInput(((values?.surchargeAmount ?? 0) / 100).toString());
  }, [values?.surchargeAmount]);

  const handleStickyNotesChange = (checked) => {
    const new_display_settings = {
      ...values?.displaySettings,
      sticky_notes: {
        alert: checked
      }
    };
    setFieldValue('displaySettings', new_display_settings);
    setFieldValue('stickyNotesAlert', checked);
  };

  const handleTechnologyFeeToggle = (checked) => {
    if (checked) {
      setShowTechnologyModal(true);
    } else {
      setFieldValue('surchargeEnabled', false);
    }
  };

  const handleFeePercentageChange = (e) => {
    const next = e.target.value;
    // Allow only digits with at most one decimal place (e.g. "3", "3.", "3.3").
    if (/^\d*\.?\d?$/.test(next)) {
      setFeeInput(next);
    }
  };

  const handleFeePercentageBlur = () => {
    const numValue = parseFloat(feeInput);

    if (Number.isNaN(numValue)) {
      setFeeInput('0');
      setFieldValue('surchargeAmount', 0);
      return;
    }

    if (numValue > 6) {
      showAlert({
        title: 'Invalid fee percentage',
        message: 'Fee percentage cannot exceed 6%. Value will be set to 6%.',
        color: 'warning'
      });
    } else if (numValue < 0) {
      showAlert({
        title: 'Invalid fee percentage',
        message: 'Fee percentage cannot be negative. Value will be set to 0%.',
        color: 'warning'
      });
    }

    const limitedValue = Math.min(Math.max(numValue, 0), 6);
    // Round to one decimal so surchargeAmount stays a clean integer, avoiding
    // float artifacts (e.g. 3.3 * 100 -> 330.00000000000006) and stepper drift.
    const surchargeAmount = Math.round(limitedValue * 10) * 10;
    setFieldValue('surchargeAmount', surchargeAmount);
    setFeeInput((surchargeAmount / 100).toString());
  };

  const Divider = () => <div className="h-[1px] bg-neutral-200" />;

  return (
    <>
      <SectionList
        title="Operational settings"
        description="Configure practice-specific options like technology fee, sticky notes, and batch time preferences.">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <Switch
              checked={values?.stickyNotesAlert}
              onChange={handleStickyNotesChange}
              id="sticky-notes-switch"
            />
            <label htmlFor="sticky-notes-switch" className="text-sm font-400 text-neutral-900">
              Automatically open sticky notes
            </label>
          </div>

          <Allowed requiredModules="medipay.technology_fee">
            <Divider />

            {/* Technology fee */}
            <div className="flex items-center gap-2">
              <Switch
                checked={values?.surchargeEnabled}
                onChange={handleTechnologyFeeToggle}
                id="technology-fee-switch"
              />
              <label htmlFor="technology-fee-switch" className="text-sm font-400 text-neutral-900">
                Technology fee
              </label>
            </div>

            {/* Fee percentage - only show if technology fee is enabled */}
            {values?.surchargeEnabled && (
              <Input
                label="Fee percentage"
                value={feeInput}
                onChange={handleFeePercentageChange}
                onBlur={handleFeePercentageBlur}
                type="number"
                min={0}
                max={6}
                step="any"
                className="max-w-[125px]"
                error={
                  parseFloat(feeInput) > 6
                    ? 'Fee cannot exceed 6%'
                    : parseFloat(feeInput) < 0
                      ? 'Fee cannot be negative'
                      : null
                }
              />
            )}
          </Allowed>
        </div>
      </SectionList>

      <Divider />

      {/* Payment batch time section */}
      <SectionList
        title="Payment batch time"
        description="Daily cutoff for batching and transferring processed payments to your bank.">
        <div className=" flex flex-col gap-2">
          <label className="text-sm font-500 text-neutral-800">Transaction batch time</label>
          {/* For now this will be read only */}
          <Input value="07:00 AM, EST" disabled className="w-max" icon="clock-mono" />
        </div>
      </SectionList>
    </>
  );
};

export default withErrorBoundary(OperationalSettings);
